import { useMutation } from '@tanstack/react-query';
import { useQuery } from '@apollo/client/react';
import { WorkoutCompletionStatus, WorkoutUtils } from '@fytness/shared';
import { formatISO, parseISO } from 'date-fns';
import { useEffect, useCallback } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { CurrentUserDocument } from '~/graphql/generated/graphql';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/date-picker/DatePicker';
import { useHistoryMinDate } from '~/calendar/hooks/useHistoryMinDate';
import { WorkoutStructure } from '~/workout/components/workout-structure/WorkoutStructure';
import { WorkoutFormActions } from '~/workout/components/workout-form-actions/WorkoutFormActions';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import type { WorkoutRecord } from '~/powersync/generated/schema';
import { WorkoutMobileHeader } from '~/workout/components/workout-mobile-header/WorkoutMobileHeader';
import { WorkoutStartStopButton } from '~/workout/components/workout-start-stop-button/WorkoutStartStopButton';
import { ScreenBottomBar } from '~/layout/components/screen-bottom-bar/ScreenBottomBar';

export interface WorkoutDetailProps {
  workoutNiceId: string;
}

interface WorkoutDetailFormData {
  name: string;
  plannedAt: Date;
  note: string;
}

export const WorkoutDetail = ({ workoutNiceId }: WorkoutDetailProps) => {
  const { t } = useTranslation('workout');
  const minimumDate = useHistoryMinDate();
  const powerSync = usePowerSync();

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUserId = userData?.currentUser?.id;

  const { data: existingWorkout, isLoading: loadingWorkout } =
    useLivePowerSyncQuery<WorkoutRecord>(
      db.Workout.queries.findByNiceId(workoutNiceId),
      [workoutNiceId],
      { single: true },
    );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<WorkoutDetailFormData>({
    defaultValues: {
      name: '',
      plannedAt: new Date(),
      note: '',
    },
  });

  useEffect(() => {
    if (existingWorkout) {
      reset({
        name: existingWorkout.name || '',
        plannedAt: existingWorkout.plannedAt
          ? parseISO(existingWorkout.plannedAt.slice(0, 10))
          : new Date(),
        note: existingWorkout.note || '',
      });
    }
  }, [existingWorkout, reset]);

  const updateWorkoutMutation = useMutation({
    mutationFn: (data: {
      name?: string;
      plannedAt?: string;
      note?: string;
    }) => {
      if (!existingWorkout) return;

      return db.Workout.mutations.update(powerSync, existingWorkout.id, data);
    },
  });

  const handleSave = useCallback(
    (data: WorkoutDetailFormData) => {
      updateWorkoutMutation.mutate({
        name: data.name,
        plannedAt: formatISO(data.plannedAt, { representation: 'date' }),
        note: data.note,
      });
    },
    [updateWorkoutMutation],
  );

  if (loadingWorkout) {
    return (
      <ScrollView className="flex-1 bg-background">
        <View className="p-4">
          <Card>
            <CardContent>
              <Text className="text-muted-foreground">Loading workout...</Text>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    );
  }

  if (!existingWorkout) {
    return (
      <ScrollView className="flex-1 bg-background">
        <View className="p-4">
          <Card>
            <CardContent>
              <Text className="text-destructive">Workout not found</Text>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    );
  }

  return (
    <View className="flex-1 bg-background">
      <ScrollView className="flex-1">
        <WorkoutMobileHeader
          title={existingWorkout.name || t('form.title.detail')}
          workout={existingWorkout}
          className="md:hidden"
        />
        <View className="p-4 gap-4">
          {/* Planned date: mobile only — desktop uses the header date picker */}
          <View className="gap-2 md:hidden">
            <Controller
              control={control}
              name="plannedAt"
              rules={{ required: t('form.validation.dateRequired') }}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  value={value}
                  onChange={onChange}
                  label={t('form.fields.plannedAt.label')}
                  minimumDate={minimumDate ?? undefined}
                />
              )}
            />
            {errors.plannedAt && (
              <Text className="text-destructive text-xs">
                {errors.plannedAt.message}
              </Text>
            )}
          </View>
          <Card className="bg-surface rounded-xl p-4 gap-4">
            <View className="gap-4">
              <View className="gap-2">
                <Text className="text-xs font-medium text-text-secondary">
                  {t('form.fields.name.label')}
                </Text>
                <Controller
                  control={control}
                  name="name"
                  rules={{ required: t('form.validation.nameRequired') }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder={t('form.fields.name.placeholder')}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      className="bg-surface-elevated rounded-lg px-3 py-3 border-0 text-sm font-regular text-text-primary"
                      placeholderClassName="text-text-muted"
                    />
                  )}
                />
                {errors.name && (
                  <Text className="text-destructive text-xs">
                    {errors.name.message}
                  </Text>
                )}
              </View>

              <View className="gap-2">
                <Text className="text-xs font-medium text-text-secondary">
                  {t('form.fields.note.label')}
                </Text>
                <Controller
                  control={control}
                  name="note"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder={t('form.fields.note.placeholder')}
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      multiline
                      className="bg-surface-elevated rounded-lg px-3 py-3 border-0 text-sm font-regular text-text-primary"
                      placeholderClassName="text-text-muted"
                    />
                  )}
                />
              </View>

              {isDirty && (
                <View className="mt-2">
                  <Button
                    className="flex-1"
                    onPress={handleSubmit(handleSave)}
                    loading={updateWorkoutMutation.isPending}
                  >
                    <Text>{t('form.buttons.save')}</Text>
                  </Button>
                </View>
              )}
            </View>
          </Card>

          <WorkoutStructure
            workoutId={existingWorkout.id}
            isLoading={updateWorkoutMutation.isPending}
          />

          <WorkoutFormActions
            isEditMode={true}
            isLoading={updateWorkoutMutation.isPending}
            workoutId={existingWorkout.id}
          />
        </View>
      </ScrollView>

      {WorkoutUtils.getCompletionStatus(existingWorkout) ===
        WorkoutCompletionStatus.PLANNED && (
        <ScreenBottomBar className="flex-row justify-end">
          <WorkoutStartStopButton workout={existingWorkout} />
        </ScreenBottomBar>
      )}
    </View>
  );
};
